import {
  EuiBadge,
  EuiCallOut,
  EuiComboBox,
  EuiComboBoxOptionOption,
  EuiDescriptionList,
  EuiFlexGroup,
  EuiFlexItem,
  EuiForm,
  EuiFormRow,
  EuiPanel,
  EuiSpacer,
  EuiText,
} from "@elastic/eui";
import {
  InviteUser,
  KeycloakOrganizationUser,
  useAnketaAuth,
  useAnketaI18n,
} from "../../../";
import { useEffect, useMemo, useState } from "react";
import { CreateNewUser } from "./createNew";
import { isValidEmail, isValidEmailUser } from "../../../";
import {
  DomainOption,
  SelectOrInviteMemberProps,
} from "src/questionnaires/types";

export const SelectOrInviteMember = ({
  availableMembers,
  members,
  setMembers,
  newUsers,
  setNewUsers,
  domains,
}: SelectOrInviteMemberProps): JSX.Element => {
  const { translate } = useAnketaI18n();
  const { user } = useAnketaAuth();
  const [callOutMessage, setCallOutMessage] = useState<string>("");

  const [selectedDomain, setSelectedDomain] = useState<
    EuiComboBoxOptionOption<DomainOption> | undefined
  >(undefined);

  const onChange = (
    selectedOptions: Array<EuiComboBoxOptionOption<KeycloakOrganizationUser>>,
  ) => {
    setMembers(selectedOptions);
  };

  const updateNewUsers = (selectedOptions: InviteUser) => {
    setNewUsers((prevSelected) => {
      const exists = prevSelected.some(
        (opt) => opt.value.email === selectedOptions.email,
      );
      if (exists) {
        return prevSelected.map((opt) =>
          opt.value.email === selectedOptions.email
            ? { ...opt, value: selectedOptions }
            : opt,
        );
      }
      return [
        ...prevSelected,
        {
          value: selectedOptions,
          label: selectedOptions.email,
          color: "rgb(255, 102, 102)",
        },
      ];
    });
  };

  const removeNewUser = (toRemove: InviteUser) => {
    setNewUsers((prevSelected) =>
      prevSelected.filter((opt) => opt.value.email !== toRemove.email),
    );
  };

  const newUserRow = useMemo<Array<JSX.Element>>(() => {
    if (newUsers && newUsers.length > 0) {
      return newUsers.map((newUser) => (
        <EuiFlexItem key={newUser.label}>
          <CreateNewUser
            email={newUser.label}
            updateNewUsers={updateNewUsers}
            removeNewUser={removeNewUser}
            firstName={newUser.value ? newUser.value.firstName : ""}
            lastName={newUser.value ? newUser.value.lastName : ""}
            organizations={newUser.value ? newUser.value.organizations : ""}
          />
        </EuiFlexItem>
      ));
    }
    return [];
  }, [newUsers]);

  const newUserBadges = useMemo<Array<JSX.Element>>(() => {
    return newUsers.flatMap((option) => {
      if (option.value) {
        return (
          <EuiFlexItem key={`${option.value.email}`} grow={false}>
            <EuiBadge color="warning">{option.value.email}</EuiBadge>
          </EuiFlexItem>
        );
      }
      return [];
    });
  }, [newUsers]);

  useEffect(() => {
    const customerDomains = domains.filter(
      (domain) =>
        !domain.domain.includes("@autonubil.") &&
        !domain.domain.includes("@medocino."),
    );
    if (customerDomains.length > 0) {
      setSelectedDomain({
        label: customerDomains[0].domain,
        value: customerDomains[0],
      });
    }
  }, [domains]);

  useEffect(() => {
    if (user) {
      const self = availableMembers.find(
        (member) => member.value.email === user.email,
      );
      if (self) {
        onChange([self]);
      }
    }
  }, [availableMembers, user]);

  const onCreateOption = (searchValue: string) => {
    const normalizedSearchValue = searchValue.trim().toLowerCase();

    if (!selectedDomain) {
      setCallOutMessage(
        translate("anketa.create.missing.field", {
          values: { field: translate("anketa.domain") },
        }) || "Please select a domain before adding a user.",
      );
      return;
    }

    const newEmail = `${searchValue}${selectedDomain.value.domain}`;

    if (!isValidEmailUser(searchValue) || !isValidEmail(newEmail)) {
      setCallOutMessage(
        translate("anketa.create.missing.field", {
          values: { field: translate("anketa.user.email") },
        }),
      );
      return;
    }

    if (!normalizedSearchValue) return;

    setCallOutMessage("");
    const value: InviteUser = {
      email: newEmail,
      firstName: "",
      lastName: "",
      organizations: selectedDomain.value.organizationAlias,
    };
    const newOption = {
      label: newEmail,
      color: "danger",
      value,
    };
    setNewUsers((prevSelected) => [...prevSelected, newOption]);
  };

  const hasSelectedMembers = members.length > 0;
  const hasNewUsers = newUsers.length > 0;
  const showMemberPanel = hasSelectedMembers || hasNewUsers;

  return (
    <EuiForm>
      {callOutMessage && (
        <>
          <EuiCallOut
            title={callOutMessage}
            color="danger"
            iconType="alert"
            size="s"
          />
          <EuiSpacer size="m" />
        </>
      )}

      <EuiFormRow
        label={translate("anketa.members")}
        helpText={translate("anketa.user.email.username.helper")}
        fullWidth
      >
        <EuiFlexGroup gutterSize="s" responsive={false}>
          <EuiFlexItem style={{ flexBasis: "66%" }}>
            <EuiComboBox
              placeholder={translate("anketa.create.selection", {
                values: { type: translate("anketa.members") },
              })}
              options={
                availableMembers && availableMembers.length > 0
                  ? availableMembers
                  : []
              }
              selectedOptions={members && members.length > 0 ? members : []}
              onChange={onChange}
              noSuggestions={availableMembers.length <= 1}
              onCreateOption={onCreateOption}
            />
          </EuiFlexItem>
          <EuiFlexItem style={{ flexBasis: "34%" }}>
            <EuiComboBox
              singleSelection={{ asPlainText: true }}
              placeholder={translate("anketa.domain.select") || "Select domain"}
              options={domains.map((domain) => ({
                label: domain.domain,
                value: domain,
              }))}
              selectedOptions={selectedDomain ? [selectedDomain] : []}
              onChange={(selected) => {
                setSelectedDomain(selected[0]);
                if (selected[0]) setCallOutMessage("");
              }}
            />
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFormRow>

      {showMemberPanel && (
        <>
          <EuiSpacer size="m" />
          <EuiPanel color="subdued" paddingSize="m">
            {hasSelectedMembers && (
              <EuiDescriptionList
                listItems={members.map((member) => ({
                  title: member.value.email,
                  description: member.value.username
                    ? `${member.value.username} (${member.value.firstName} ${member.value.lastName})`
                    : translate("anketa.members.created.invited"),
                }))}
              />
            )}
            {hasNewUsers && (
              <>
                {hasSelectedMembers && <EuiSpacer size="s" />}
                <EuiText size="xs" color="subdued">
                  <p style={{ marginBottom: 6 }}>
                    {translate("anketa.members.new.pending") ||
                      "Pending invitations"}
                  </p>
                </EuiText>
                <EuiFlexGroup wrap gutterSize="s">
                  {newUserBadges}
                </EuiFlexGroup>
              </>
            )}
          </EuiPanel>
        </>
      )}

      {newUserRow.length > 0 && (
        <>
          <EuiSpacer size="m" />
          <EuiFlexGroup direction="column" gutterSize="m">
            {newUserRow}
          </EuiFlexGroup>
        </>
      )}
    </EuiForm>
  );
};
